<script lang="ts">
	import { Alert } from "flowbite-svelte-v1";
	import { getContext } from "svelte";
	import { flip } from "svelte/animate";
	import { twMerge } from "tailwind-merge";
	import type { AssetRef } from "#gen/bundles/v1/bundle_pb.js";
	import { ImageSchema, type Image, type ImageJson } from "#gen/bundles/v1/common_pb.js";
	import { DataType } from "#gen/common/v1/types_pb.js";
	import Icon from "#lib/components/Icon.svelte";
	import P from "#lib/components/P.svelte";
	import Toggle from "#lib/components/Toggle.svelte";
	import PlaintextEditor from "#lib/components/content-editors/PlaintextEditor.svelte";
	import ImageTile from "#lib/components/flow/editor/ImageTile.svelte";
	import ImageAppearanceEditor from "#lib/components/flow/editor/appearance/ImageAppearanceEditor.svelte";
	import StateEditor from "#lib/components/flow/editor/appearance/StateEditor.svelte";
	import { SelectionType, type Selection } from "#lib/components/flow/editor/builder/types.js";
	import AddImage from "#lib/components/images/AddImage.svelte";
	import EditImage from "#lib/components/images/EditImage.svelte";
	import type { ImagePickerChangeEvent } from "#lib/components/images/types.js";
	import { isDataProperty } from "#lib/components/images/utils.js";
	import type { ComponentEditingProps } from "#lib/components/page/ComponentProps.js";
	import EditorInset from "#lib/components/page/editing/EditorInset.svelte";
	import type { BundleDag } from "#lib/dag.js";
	import { getImageDimensions } from "#lib/image.js";
	import FeatureFlagGate from "#lib/permissions/FeatureFlagGate.svelte";
	import { create, type JsonObject, type JsonValue } from "#lib/protobuf-compat.js";
	import { listValue, numberValue, setListValue, setNumberValue, setStringValue, setStructValue, stringValue, structValue, unsetValue } from "#lib/struct.js";
	import type { TokenProvider } from "#lib/tokenProvider.js";
	import type { Writable } from "svelte/store";

	let { id, page, onChange }: ComponentEditingProps = $props();

	let lastError: unknown = $state();

	const tokenProvider: Writable<TokenProvider> = getContext("tokenProvider");
	const dag: Writable<BundleDag<AssetRef>> | undefined = getContext("dag");
	const selection: Writable<Selection | undefined> | undefined = getContext("selection");

	const hasArrayTokens = $derived($tokenProvider.hasTokensOfType(DataType.ARRAY));
	const images = $derived(listValue(structValue($page.config, id), "images") || []);
	const src = $derived(stringValue(structValue($page.config, id), "src") || firstImageUrl(images));
	const aspectRatio = $derived(numberValue(structValue($page.config, id), "aspectRatio"));
	const className = $derived(stringValue(structValue($page.config, id), "class"));
	const imagesKey = $derived(stringValue(structValue($page.config, id), "imagesKey"));
	const globalLastError: Writable<Error | undefined> = getContext("lastError");

	const workingImages: () => JsonValue[] = $derived(() => {
		if (images.length > 0) return images;
		if (src) return [create(ImageSchema, { url: src, focalPoint: undefined })] as unknown as JsonValue[];
		return [];
	});

	const hasDestination = $derived.by(() => {
		const selected = selection && $selection;
		if (!dag || !$dag || !selected || selected.item.type !== SelectionType.NODE) {
			return false;
		}
		return $dag.getNode(selected.item.value.id)?.linksOut.some(link => link.sourceAnchor === id) ?? false;
	});

	function commitImages(imgs: JsonValue[]) {
		const config = getConfig();
		setListValue(config, "images", imgs);

		if (config.src) delete config.src;
		if (imgs.length < 1) delete config.aspectRatio;

		$page = $page;
		onChange?.(imgs);
	}

	function getConfig(): JsonObject {
		let config = structValue($page.config, id);
		if (!config) {
			config = {} as JsonObject;
			setStructValue($page.config, id, config);
		}
		return config;
	}

	function asImage(v: JsonValue): Image {
		return v as unknown as Image;
	}

	function firstImageUrl(imgs: JsonValue[] | undefined): string | undefined {
		return imgs?.[0] ? asImage(imgs[0]).url : undefined;
	}

	async function saveImage(e: CustomEvent<ImagePickerChangeEvent>) {
		const config = getConfig();
		const url = e.detail.value?.trim() ?? "";
		const img = create(ImageSchema, { url, focalPoint: e.detail.focalPoint });
		const imgs = [...workingImages(), img as unknown as JsonValue];
		if (imgs.length === 1 && url && !isDataProperty(url)) {
			try {
				const { width, height } = await getImageDimensions(url);
				setNumberValue(config, "aspectRatio", width / height);
			} catch {
				$globalLastError = new Error("Couldn't read the image size. Saved without an aspect ratio.");
			}
		}
		commitImages(imgs);
	}

	async function updateImage(e: CustomEvent<ImageJson>, index: number) {
		const config = getConfig();
		const imgs = [...workingImages()];
		const url = e.detail?.url?.trim() ?? "";
		if (index === 0 && url && !isDataProperty(url)) {
			try {
				const { width, height } = await getImageDimensions(url);
				setNumberValue(config, "aspectRatio", width / height);
			} catch {
				$globalLastError = new Error("Couldn't read the image size. Saved without an aspect ratio.");
			}
		}
		if (!e.detail) {
			imgs.splice(index, 1);
		} else {
			imgs[index] = {
				url,
				focalPoint: e.detail.focalPoint ?? null,
			};
		}
		commitImages(imgs);
	}

	function removeImage(index: number) {
		const imgs = workingImages().filter((_, i) => i !== index);
		commitImages(imgs);
	}

	function dragstart(event: DragEvent, i: number) {
		if (!event.dataTransfer) return;

		event.dataTransfer.effectAllowed = "move";
		event.dataTransfer.setData("text/plain", "" + i);
	}

	function drop(event: DragEvent, target: number) {
		if (!event.dataTransfer) return;

		const start = Number.parseInt(event.dataTransfer.getData("text/plain"));
		const imgs = workingImages();

		if (start < target) {
			imgs.splice(target + 1, 0, imgs[start]);
			imgs.splice(start, 1);
		} else {
			imgs.splice(target, 0, imgs[start]);
			imgs.splice(start + 1, 1);
		}
		commitImages(imgs);
	}

	function updateImageKey(e: CustomEvent<string | undefined>) {
		const config = getConfig();

		setStringValue(config, "imagesKey", e.detail ?? "");
		setListValue(config, "images", []);

		$page = $page;
		onChange?.(e.detail);
	}

	function handleImagesDataPropertyToggle(e: Event) {
		const target = e.target as HTMLInputElement;

		const config = getConfig();

		if (target.checked) {
			const token = $tokenProvider.tokens().find(t => t.dataType === DataType.ARRAY);
			setStringValue(config, "imagesKey", token?.value ?? "");
		} else {
			unsetValue(config, "imagesKey");
		}
		setListValue(config, "images", []);

		$page = $page;
		onChange?.(target.checked);
	}

	export function validate(): boolean {
		return true;
	}

	function updateLegacySrc(e: CustomEvent<ImagePickerChangeEvent>) {
		if (images.length > 0) {
			const imgs = [...images];
			const url = e.detail.value?.trim() ?? "";
			if (url) {
				imgs[0] = create(ImageSchema, { url, focalPoint: e.detail.focalPoint }) as unknown as JsonValue;
			} else {
				imgs.splice(0, 1);
			}
			commitImages(imgs);
			return;
		}

		const config = getConfig();
		setStringValue(config, "src", e.detail.value);
		unsetValue(config, "images");
		$page = $page;
		onChange?.(e.detail as unknown as JsonValue);
	}
</script>

<EditorInset>
	<div class="px-6">
		<FeatureFlagGate flag="build_your_own_page">
			<div class="flex flex-col gap-6">
				<div class="flex w-full flex-col gap-5 rounded-lg border p-3">
					<P class="text-sm font-bold text-black"><FeatureFlagGate flag="editor_toolbar_revamp_q4_2026">Content{#snippet fallback()}Attributes{/snippet}</FeatureFlagGate></P>
					<div class="flex flex-col gap-2">
						<div class="flex items-center justify-between gap-2">
							<P class="text-xs font-semibold tracking-wide text-gray-500 uppercase">Images</P>
							{#if hasArrayTokens}
								<div class="flex items-center gap-2">
									<P size="sm">Use data property</P>
									<Toggle size="small" checked={imagesKey !== undefined} on:change={handleImagesDataPropertyToggle}></Toggle>
								</div>
							{/if}
						</div>
						<div class="flex gap-2 border-l-[5px] border-l-gray-300 pl-6">
							{#if hasArrayTokens && imagesKey !== undefined}
								<PlaintextEditor class="grow" id="images" provider={$tokenProvider} tokenFilter={dt => dt === DataType.ARRAY} value={imagesKey} on:change={updateImageKey} aria-labelledby="images-label" disableSpace />
							{:else}
								<div class="flex flex-col gap-2">
									<div class="grid grid-cols-6 gap-2">
										{#each workingImages() as v, i (v)}
											{@const photo = asImage(v)}
											<div animate:flip={{ duration: 200 }}>
												{#if photo}
													<ImageTile
														class="h-30 w-30"
														photo={photo.url}
														focalPoint={photo.focalPoint}
														on:remove={() => removeImage(i)}
														on:dragstart={event => dragstart(event, i)}
														on:drop={event => drop(event, i)}
														on:change={e => updateImage(e, i)}
														aspectRatio={i !== 0 ? aspectRatio : undefined}
														initialAspectRatio={aspectRatio}
														allowFocalPointSelection={true}
														showAspectRatioPicker={true}
													/>
												{/if}
											</div>
										{/each}
										<AddImage on:change={saveImage} dropzoneClass="h-30 w-30 aspect-square" {aspectRatio} allowFocalPointSelection={true} showAspectRatioPicker={true} />
									</div>
									{#if workingImages().length > 1}
										<Alert color="gray" class="my-2 flex items-start">
											<Icon iconSet="heroicons" name="exclamation-circle" class="mt-0.5 shrink-0 text-gray-500" height={16} width={16} />
											<P size="xs"><span class="font-semibold">Please note:</span> All images inherit the first image's aspect ratio. To change this, adjust the crop on the first image.</P>
										</Alert>
									{/if}
								</div>
							{/if}
						</div>
					</div>
				</div>
				<div class="flex w-full flex-col gap-2 rounded-lg border p-3">
					<ImageAppearanceEditor {id} {page} {onChange} />
				</div>
				<StateEditor {id} {page} {onChange} showEnableClick={hasDestination} />
			</div>
			{#snippet fallback()}
				<EditImage class="h-40 w-40" imgClass={twMerge("h-auto", className)} bind:error={lastError} value={src} on:change={updateLegacySrc} {aspectRatio} initialAspectRatio={aspectRatio} showAspectRatioPicker={true} />
			{/snippet}
		</FeatureFlagGate>
	</div>
</EditorInset>
